import React from 'react';
import { type Theme, useStyletron } from 'baseui';
import type { StyleObject } from 'styletron-react';
import { SPAN_EDGE, TIMELINE_DRAG_ATTR } from '../../../constants/frame-timeline-v4';
import type { SpanPositionT } from '../../../types/frame-timeline-v4';
import { getSpanEdgeValues, hasEndEdge, hasStartEdge } from '../../../utils/frame-timeline-v4';
type SpanPieceOverridesT = {
  fillColor?: string;
  borderColor?: string;
  handleColor?: string;
  borderTransition?: boolean;
  borderDashed?: boolean;
};
type SpanPiecePropsT = {
  position: SpanPositionT;
  cellWidth: number;
  overrides?: SpanPieceOverridesT;
  children?: React.ReactNode;
  resizable?: boolean;
  clickable?: boolean;
};
const getSpanBarStyle = (theme: Theme, position: SpanPositionT, fillColor: string, borderColor: string, clickable: boolean, borderTransition: boolean, borderDashed: boolean): StyleObject => {
  const edge = theme.sizing.scale0;
  const {
    startSide,
    endSide,
    startRadius,
    endRadius,
    startInset,
    endInset
  } = getSpanEdgeValues(theme, position);
  return {
    boxSizing: 'border-box',
    position: 'absolute',
    top: '0px',
    bottom: '0px',
    marginTop: 'auto',
    marginBottom: 'auto',
    left: startInset,
    right: endInset,
    height: theme.sizing.scale700,
    overflow: 'hidden',
    backgroundColor: fillColor,
    borderStyle: borderDashed ? 'dashed' : 'solid',
    borderColor,
    borderTopWidth: edge,
    borderBottomWidth: edge,
    borderLeftWidth: startSide,
    borderRightWidth: endSide,
    borderRadius: `${startRadius} ${endRadius} ${endRadius} ${startRadius}`,
    cursor: clickable ? 'pointer' : 'default',
    ...(borderTransition ? {
      transition: 'border-color 150ms ease-in-out'
    } : {})
  };
};

// Invisible pointer target: spans the full bar height and is much wider than the visual pill so
// the edge is easy to grab. Capped at 45% so start/end targets never overlap on a 1-frame span.
const getHandleHitAreaStyle = (theme: Theme, side: 'left' | 'right', interactive: boolean, cellWidth: number): StyleObject => ({
  position: 'absolute',
  top: '0px',
  bottom: '0px',
  [side]: '0px',
  width: `${cellWidth}px`,
  maxWidth: '45%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
  paddingLeft: side === 'left' ? theme.sizing.scale0 : '0px',
  paddingRight: side === 'right' ? theme.sizing.scale0 : '0px',
  pointerEvents: interactive ? 'auto' : 'none',
  cursor: interactive ? 'ew-resize' : 'default'
});

// Visual only — the surrounding hit-area element owns the pointer target and drag attributes.
const getHandlePillStyle = (theme: Theme, handleColor: string): StyleObject => ({
  flex: '0 0 auto',
  width: theme.sizing.scale0,
  height: theme.sizing.scale500,
  borderRadius: theme.borders.radius100,
  backgroundColor: handleColor,
  pointerEvents: 'none'
});
export const SpanPiece = ({
  position,
  cellWidth,
  overrides,
  children,
  resizable = false,
  clickable = false
}: SpanPiecePropsT) => {
  const [css, theme] = useStyletron();
  const fill = overrides?.fillColor ?? theme.colors.backgroundSecondary;
  const border = overrides?.borderColor ?? theme.colors.borderOpaque;
  const handle = overrides?.handleColor ?? theme.colors.contentTertiary;
  const animate = overrides?.borderTransition ?? false;
  const dashed = overrides?.borderDashed ?? false;

  // The gesture provider starts a drag from any [data-timeline-drag] element and the resize
  // controller reads the edge from [data-span-edge]; both are only attached when resizable.
  const dragProps = resizable ? {
    [TIMELINE_DRAG_ATTR]: ''
  } : {};
  return <div data-testid="span-piece" className={css(getSpanBarStyle(theme, position, fill, border, clickable, animate, dashed))}>
      {children}
      {hasStartEdge(position) ? <div data-testid="span-handle-start" data-span-edge={resizable ? SPAN_EDGE.START : undefined} {...dragProps} className={css(getHandleHitAreaStyle(theme, 'left', resizable, cellWidth))}>
          <span className={css(getHandlePillStyle(theme, handle))} />
        </div> : null}
      {hasEndEdge(position) ? <div data-testid="span-handle-end" data-span-edge={resizable ? SPAN_EDGE.END : undefined} {...dragProps} className={css(getHandleHitAreaStyle(theme, 'right', resizable, cellWidth))}>
          <span className={css(getHandlePillStyle(theme, handle))} />
        </div> : null}
    </div>;
};